/* eslint-disable react/no-unescaped-entities */
/**
 * iter 144w - Cost of Living Calculator V2 (Timeleft-style).
 *
 * Lives at the same URL the old calculator did
 * (`/mexico-cost-of-living-calculator`) so all the SEO equity is
 * preserved. The flow is intentionally no-nav / no-footer: one
 * question per full screen, amber progress bar at top, auto-advance
 * 300ms after a selection, breakpoint affirmations between key
 * questions, mid-flow email gate, persona-driven results page with
 * exit-intent overlay.
 *
 * URL params:
 *   ?promo=LAUNCH25  pre-captured by App.js → getStoredPromo()
 *   ?utm_*=          pre-captured by App.js → utm.js
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { getStoredPromo } from '../../lib/promo';
import { getUtm } from '../../lib/utm';
import { BRAND_LOGO } from '../../constants/brand';
import {
  trackCalculatorV2Started,
  trackCalculatorV2EmailCaptured,
  trackCalculatorV2Completed,
  trackCalculatorV2CtaClicked,
  trackCalculatorCheckpointReached,
  trackCalculatorCheckpointCaptured,
  trackQuizExitIntentShown,
  trackQuizExitIntentConverted,
  trackQuizExitIntentDismissed,
} from '../../lib/analytics';
import {
  VISA_THRESHOLDS,
  approx,
  PER_APPLICANT_LABEL,
  STANDARD_DISCLAIMER,
  DEPENDENT_DISCLAIMER,
} from '../../content/visaThresholds';

const TR = VISA_THRESHOLDS.temporary_resident;
// Standard Temporary Residency income threshold - use everywhere the calc
// mentioned "proof of income" for any of the income-source personas. This
// used to differ per persona (leftover legacy $2,700 numbers) - the SSoT
// collapses them into one accurate figure.
const TR_MONTHLY = TR.monthly_income_usd;   // 4400
const TR_SAVINGS = TR.savings_usd;          // 74000

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const STORAGE_KEY = 'mundalo_calc_answers';

/* =========================================================================
 * Data tables - kept inline so this whole flow is self-contained.
 * ========================================================================= */

/* =========================================================================
 * iter 145 (Section 5) - "I'll keep exploring" - today-only QUIZ50 offer.
 *
 * Renders a subtle secondary link below the primary CTA. When clicked,
 * the link is replaced by a card with a midnight countdown timer + a
 * "Claim 50% Off - $99" button that routes to Stripe checkout with the
 * QUIZ50 promo applied. Once revealed in a session it stays revealed.
 * ========================================================================= */

const _SESSION_FLAG_KEY = 'mundalo_calc_keepexploring_revealed';

function KeepExploringOffer({ onConvert }) {
  const [revealed, setRevealed] = useState(() => {
    try { return window.sessionStorage?.getItem(_SESSION_FLAG_KEY) === '1'; }
    catch { return false; }
  });
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!revealed) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [revealed]);
  if (!revealed) {
    return (
      <button
        type="button"
        data-testid="calc-keep-exploring-link"
        onClick={() => {
          try { window.sessionStorage?.setItem(_SESSION_FLAG_KEY, '1'); } catch { /* noop */ }
          setRevealed(true);
        }}
        className="block mx-auto text-sm text-[#F4ECD8]/55 hover:text-[#F4ECD8]/85 underline underline-offset-4 transition-colors py-3"
      >
        I'll keep exploring for now.
      </button>
    );
  }
  // Midnight in user's LOCAL timezone.
  const midnight = (() => {
    const d = new Date();
    d.setHours(24, 0, 0, 0);
    return d.getTime();
  })();
  const ms = Math.max(0, midnight - now);
  const hh = String(Math.floor(ms / 3.6e6)).padStart(2, '0');
  const mm = String(Math.floor((ms % 3.6e6) / 6e4)).padStart(2, '0');
  const ss = String(Math.floor((ms % 6e4) / 1000)).padStart(2, '0');
  return (
    <div
      data-testid="calc-keep-exploring-offer"
      className="bg-white/[0.04] border-2 border-[#E8A830] rounded-2xl p-5 md:p-6 mt-2"
    >
      <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#E8A830] mb-3">
        Today-only offer
      </p>
      <p className="text-[#F4ECD8] text-base md:text-lg leading-relaxed mb-4">
        Before you go - your results expire at midnight. Lock in your
        Blueprint for <strong>50% off</strong>, today only.
      </p>
      <div className="font-mono text-[#E8A830] text-3xl md:text-4xl tracking-[0.12em] tabular-nums mb-5">
        {hh}:{mm}:{ss}
      </div>
      <button
        type="button"
        data-testid="calc-keep-exploring-claim"
        onClick={onConvert}
        className="w-full bg-[#E8A830] hover:bg-[#f0b748] text-[#1a1a1a] font-semibold py-3.5 rounded-full transition-colors"
      >
        Claim 50% Off - $99
      </button>
    </div>
  );
}


const PERSONAS = [
  { key: 'nomad',     label: 'Digital Nomad',           sub: 'testing different cities' },
  { key: 'longterm',  label: 'Long-Term Mover',         sub: 'planting roots for 5+ years' },
  { key: 'family',    label: 'Family',                  sub: 'moving with kids' },
  { key: 'empty',     label: 'Empty Nester',            sub: "kids are grown, my turn now" },
  { key: 'retiree',   label: 'Retiree',                 sub: 'ready for slower pace and lower costs' },
  { key: 'divorced',  label: 'Recently Divorced / Restarting', sub: 'fresh start' },
  { key: 'sabbatical',label: 'Sabbatical',              sub: 'taking a break 1-3 years' },
];

const AGE_OPTIONS = ['25-34', '35-44', '45-54', '55-64', '65+'];

const INCOME_OPTIONS = [
  { key: 'remote',     label: 'Fully remote job - same paycheck from the US' },
  { key: 'freelance',  label: 'Freelance or variable income' },
  { key: 'pension',    label: 'Pension or fixed income' },
  { key: 'business',   label: 'Business owner or self-employed' },
  { key: 'savings',    label: 'Living off savings' },
  { key: 'figuring',   label: 'Still figuring that out' },
];

const BUDGET_OPTIONS = [
  { key: 'under_2k',   label: 'Under $2,000 a month',         min: 0,    max: 2000 },
  { key: '2_3_5',      label: '$2,000 to $3,500 a month',     min: 2000, max: 3500 },
  { key: '3_5_5',      label: '$3,500 to $5,000 a month',     min: 3500, max: 5000 },
  { key: '5_7_5',      label: '$5,000 to $7,500 a month',     min: 5000, max: 7500 },
  { key: 'over_7_5',   label: '$7,500 and above',             min: 7500, max: 12000 },
];

const PRIORITY_OPTIONS = [
  { key: 'cost',       label: 'Cost of living - lowest possible expenses' },
  { key: 'healthcare', label: 'Healthcare access and quality' },
  { key: 'safety',     label: 'Safety and quiet community' },
  { key: 'schools',    label: 'Schools and education for my kids' },
  { key: 'lifestyle',  label: 'Culture, food, and lifestyle' },
  { key: 'expats',     label: 'English-speaking expat community' },
  { key: 'outdoors',   label: 'Nature and outdoor lifestyle' },
  { key: 'remote',     label: 'Remote work infrastructure' },
];

// Persona → affirmation paragraph rendered at Breakpoint 1.
const PERSONA_AFFIRMATION = {
  nomad:      "Testing before committing is the smartest move you can make. 43% of the people who come through Mundalo start exactly where you are - curious, not certain. Roma Norte in Mexico City has faster internet than most US cities. Mérida has coworking spaces and zero traffic. Oaxaca has the kind of quiet that makes creative people dangerously productive. Let's find which one earns a longer stay.",
  family:     "Mexico is one of the most kid-friendly countries you will find anywhere in the world. The pace is slower. The neighborhoods are built for families, not cars. Kids play outside after dinner here. They grow up bilingual without trying. The families who make this move don't talk about what they gave up - they talk about who their kids became. Let's build the plan that gets your family there.",
  empty:      "You spent years making decisions for everyone else. The kids are grown. The house is quiet. This decision is yours - maybe the first one in a long time that is entirely, completely yours. Let's find the city that fits the life you've been putting off.",
  retiree:    "Your healthcare concerns are about to get smaller. Private hospitals in Mexico City, Mérida, and Guadalajara are modern, English-speaking, and cost a fraction of what you pay at home. The visa path for retirees is one of the most straightforward Mexico offers. Let's walk through what your retirement actually looks like here.",
  divorced:   "Choosing yourself is not a small thing. It takes more courage than most people give it credit for. There is a community of people on the other side of this exact decision - people who said yes to themselves when it was terrifying - and they will tell you it was the best thing they ever did. Let's find where they landed and whether it fits you.",
  longterm:   "Planting roots changes everything. You stop being a visitor and start being a neighbor. The best restaurants are the ones nobody posts about. The best neighborhoods are the ones where people know your name. That life takes more planning to find - but it's the one worth building. Let's make sure we find the right foundation.",
  sabbatical: "One to three years is enough time to know. A lot of sabbaticals become permanent - not because people planned it that way but because they stopped being able to imagine going back. Let's make sure the city you pick earns that.",
};

// Visa eligibility breakpoint copy - keyed off persona + age bucket.
function buildVisaEligibilityCopy(persona, age) {
  const old65plus = age === '65+';
  const old55_64  = age === '55-64';
  // iter 187.f - Route retiree 55-64 through the 55-64 branch so the
  // "same income requirement, different documentation" clarification
  // reaches every retiree who is not yet 65+. Retirees who are 65+
  // still land in the pensioner branch below.
  if (old55_64 && persona === 'retiree') {
    return {
      visa: 'Pensioner or Temporary Resident',
      copy: "Strong position. Either the Pensioner path (Temporary Residency proved with pension income) or the standard Temporary Resident path fits your age and situation. Same income requirement as any Temporary Resident applicant - Mexican immigration law does not publish a separate lower threshold for pensioners. Different documentation, same number. One more answer and we'll know which one is yours.",
    };
  }
  if (old65plus || persona === 'retiree') {
    return {
      visa: 'Pensioner Visa',
      copy: "You qualify for the Pensioner path - Temporary Residency proved with pension income - and it is one of the cleanest routes Mexico offers. Renewable every four years. Minimal paperwork after the first approval. No restrictions on what you can do while you're there. The income requirement is the same as any Temporary Resident applicant; pension income is one of the qualifying proof sources. Most people in your situation are approved within 6–8 weeks of their consulate appointment. Your healthcare concerns? Private clinics in every major expat city, English-speaking doctors, costs that will genuinely surprise you.",
    };
  }
  if (old55_64) {
    return {
      visa: 'Pensioner or Temporary Resident',
      copy: "Strong position. Either the Pensioner path (Temporary Residency proved with pension income) or the standard Temporary Resident path fits your age bracket. Same income requirement, different documentation. One more answer and we'll know which one is yours.",
    };
  }
  if (persona === 'nomad') {
    return {
      visa: 'Remote Worker Visa',
      copy: "Remote Worker Visa was built for your situation. Fastest approval of any Mexico visa path. If you have a US employer or consistent client income the documentation is straightforward. Most people in your situation are living in Mexico within 90 days of deciding to go.",
    };
  }
  return {
    visa: 'Temporary Resident Visa',
    copy: "Temporary Resident Visa is your path - the most flexible option Mexico offers and the most common route for Americans under 55. The income requirement is the one thing we need to confirm, which is exactly what we're looking at next.",
  };
}

// Income type → Breakpoint 3 copy (final visa-path + proof-of-funds).
// iter 181 - proof numbers now sourced from visaThresholds SSoT so future
// consulate updates flow from a single file.
// iter 187.f - Pensioner "Visa" is colloquial shorthand for a Temporary
// Resident (Residente Temporal) visa where the applicant proves solvency
// via pension income. Under Mexican immigration law (Ley de Migración +
// Reglamento) there is NO separate lower income threshold for pensioners
// - the requirement is the same 300× UMA (~$4,400/mo in 2026) applied to
// any qualifying income source. The previous hardcoded $2,000/mo figure
// traced to outdated pre-2016 blog copy and was incorrect. All rows now
// read from the same SSoT.
const INCOME_BREAKPOINT = {
  remote: {
    visa: 'Remote Worker Visa',
    proof: TR_MONTHLY,
    copy: `Remote Worker Visa. Clean fit. You'll need proof of ${approx(TR_MONTHLY)}/month ${PER_APPLICANT_LABEL} - your employment letter and two recent pay stubs cover that. ${STANDARD_DISCLAIMER} ${DEPENDENT_DISCLAIMER} Most people in your situation are approved within 4–6 weeks of their consulate appointment. The hardest part is usually just booking the appointment.`,
  },
  pension: {
    visa: 'Pensioner Visa',
    proof: TR_MONTHLY,
    copy: `Pensioner Visa is our shorthand for Temporary Residency with pension income. You'll need proof of ${approx(TR_MONTHLY)}/month ${PER_APPLICANT_LABEL} - your pension statements and 6 months of deposit history cover that. ${STANDARD_DISCLAIMER} ${DEPENDENT_DISCLAIMER} Once approved it is one of the most stable paths Mexico offers - renewable every four years, minimal paperwork, no annual income verification. You file once and you're done for four years. That's not something most countries offer.`,
  },
  freelance: {
    visa: 'Temporary Resident Visa',
    proof: TR_MONTHLY,
    copy: `Temporary Resident Visa is your path. Consistent income averaging ${approx(TR_MONTHLY)}/month ${PER_APPLICANT_LABEL} over recent months is what you need to show - bank statements and client invoices typically work. ${STANDARD_DISCLAIMER} ${DEPENDENT_DISCLAIMER} A significant percentage of Mundalo users are freelancers. This is very doable. The key word is consistent - not perfect, consistent.`,
  },
  savings: {
    visa: 'Temporary Resident Visa',
    proof: TR_MONTHLY,
    copy: `Temporary Resident Visa works if you can show a savings balance of ${approx(TR_SAVINGS)} ${PER_APPLICANT_LABEL} or proof of consistent withdrawals that average out to the monthly requirement. ${STANDARD_DISCLAIMER} ${DEPENDENT_DISCLAIMER} More people qualify for this than expect to. Let's see if your numbers line up.`,
  },
  business: {
    visa: 'Temporary Resident Visa',
    proof: TR_MONTHLY,
    copy: `Temporary Resident Visa. Business income counts - bank statements and recent tax returns showing consistent revenue of ${approx(TR_MONTHLY)}/month ${PER_APPLICANT_LABEL} is the standard documentation. ${STANDARD_DISCLAIMER} ${DEPENDENT_DISCLAIMER} If your business has a good year on paper you are in a stronger position than you probably realize.`,
  },
  figuring: {
    visa: 'Temporary Resident Visa',
    proof: TR_MONTHLY,
    copy: "That's honest - and it's fine. Most people who come through Mundalo are still figuring out the income side. The visa path depends on what you land on, but the city, the lifestyle, the cost - all of that we can show you right now. Keep going.",
  },
};

// iter 187.o - Sanitized visa descriptions for the FREE results screen.
// Never contains a dollar figure or a week-range - both ship in the paid
// Blueprint. The verbose `INCOME_BREAKPOINT[key].copy` is still used on the
// breakpoint screen (screen 8) mid-funnel; only the terminal results
// screen (screen 12) uses these short versions.
//
// Regression: /app/backend/tests/test_iter187o_adventures_and_calc_reduction.py
//   - No `$` character
//   - No week-range pattern (\d+[-–]\d+\s*weeks?)
//   - Every INCOME_BREAKPOINT key has an entry here
const FREE_VIEW_VISA_COPY = {
  remote:    'Clean fit for W-2 remote workers. Full requirements and your specific household calculation ship in the Blueprint.',
  pension:   'Our shorthand for Temporary Residency with pension income - one of the most stable paths Mexico offers. Full requirements and your specific household calculation ship in the Blueprint.',
  freelance: 'The standard path for consistent freelance income. Full requirements and your specific household calculation ship in the Blueprint.',
  savings:   'Works when a savings balance or consistent withdrawals cover the monthly requirement. Full requirements and your specific household calculation ship in the Blueprint.',
  business:  'Business income counts - bank statements and recent tax returns are the standard documentation. Full requirements and your specific household calculation ship in the Blueprint.',
  figuring:  "The visa path depends on what income source you land on. City, lifestyle, and cost we can show you right now. Full requirements ship in the Blueprint.",
};

// Budget breakpoint (Screen 5 → Breakpoint 4).
function buildBudgetVerdict(budgetKey, proofNeeded) {
  const b = BUDGET_OPTIONS.find((x) => x.key === budgetKey);
  const cityPreview = {
    under_2k: 'Oaxaca, Mérida, smaller colonial cities',
    '2_3_5':  'Mérida, Guadalajara, Puerto Vallarta, San Miguel',
    '3_5_5':  'Mexico City, Puerto Vallarta, San Miguel, Playa del Carmen',
    '5_7_5':  'All cities, premium neighborhoods',
    over_7_5: 'All cities, full lifestyle flexibility',
  }[budgetKey] || 'Multiple options';

  // Compare midpoint of band to required proof of funds.
  const mid = (b.min + b.max) / 2;
  if (mid >= proofNeeded * 1.6) {
    return {
      verdict: 'above',
      copy: `That budget opens up the full map. Mexico City, Puerto Vallarta, San Miguel de Allende - all of them are in play. The question now is not what you can afford. It's what kind of life you actually want. That's what the next question is about.`,
      cityPreview,
    };
  }
  if (mid >= proofNeeded) {
    return {
      verdict: 'matches',
      copy: `Your budget works. It covers the proof-of-funds requirement and gives you real options. Based on everything you've told me here's where you stand: ${cityPreview}. One more question and we'll get specific about which city actually fits you - not just your budget but the life you described.`,
      cityPreview,
    };
  }
  return {
    verdict: 'under',
    copy: `Your budget is just under the proof-of-funds requirement for the visa path we discussed. Before you worry - this is more common than you think and there are real options. Does a partner's income count toward the total? Do you have savings that could supplement? Cities like Mérida and Oaxaca also stretch budgets further than most people expect. Keep going - the full picture is more flexible than this one number.`,
    cityPreview,
  };
}

// Priority → top 3 city ids (in order).
const PRIORITY_TO_CITIES = {
  healthcare: ['merida', 'guadalajara', 'mexico_city'],
  schools:    ['merida', 'mexico_city', 'san_miguel'],
  cost:       ['oaxaca', 'merida', 'guadalajara'],
  lifestyle:  ['mexico_city', 'oaxaca', 'san_miguel'],
  expats:     ['san_miguel', 'puerto_vallarta', 'merida'],
  outdoors:   ['puerto_vallarta', 'oaxaca', 'tulum'],
  remote:     ['mexico_city', 'guadalajara', 'playa_del_carmen'],
  safety:     ['merida', 'san_miguel', 'oaxaca'],
};

// City data - name + monthly cost band per budget tier + Cloudinary thumb.
// Thumbs use the permanent Mundalo Cloudinary library (`mundalo/cities/*`).
// Sized 400x400 c_fill to match the result-screen 64px square slot.
const CITY_THUMB = (slug) => `https://res.cloudinary.com/des9fk8et/image/upload/w_400,h_400,c_fill,q_auto,f_auto/mundalo/cities/${slug}.jpg`;

// iter 187.c - Reveal-card city + descriptor by persona. The city is
// picked from persona alone (Priority is asked AFTER the reveal so we
// can't use it here). The descriptor mirrors the persona choice the
// user just made - the reveal states a match to their own inputs only,
// never an unverified social-proof pattern across other members.
const PERSONA_REVEAL = {
  retiree:    { city: 'merida',              descriptor: 'someone approaching or already in retirement' },
  longterm:   { city: 'mexico_city',         descriptor: 'a long-term relocator' },
  nomad:      { city: 'mexico_city',         descriptor: 'a remote worker or nomad' },
  family:     { city: 'merida',              descriptor: 'a family making this move' },
  career:     { city: 'mexico_city',         descriptor: 'someone moving for career reasons' },
  sabbatical: { city: 'oaxaca',              descriptor: 'someone taking a sabbatical' },
  empty:      { city: 'merida',              descriptor: 'an empty-nester choosing this for yourself' },
  divorced:   { city: 'san_miguel',          descriptor: 'someone starting a new chapter' },
};

const CITY_INFO = {
  mexico_city: {
    name: 'Mexico City',
    img: CITY_THUMB('mexico-city'),
    bands: { under_2k: 1600, '2_3_5': 2400, '3_5_5': 3800, '5_7_5': 5500, over_7_5: 7800 },
    facts: {
      healthcare: ['World-class private hospitals - ABC Medical Center', 'English-speaking doctors common', 'Insurance ~$80/mo for non-emergency'],
      schools:    ['Largest international school bench in LATAM', 'Polanco / Roma / Condesa hubs', 'Most US-accredited K-12 in country'],
      lifestyle:  ['Two world-class museums per week', 'Coffee culture you cannot replicate', 'Eternal spring climate'],
      remote:     ['1 Gbps fiber widely available', 'Hundreds of coworking spaces', 'CST-aligned timezone'],
      cost:       ['Bigger spend but bigger options', 'Public transit cuts car costs', 'Trade-up neighborhoods exist'],
      expats:     ['~50,000 American expats', 'Tight-knit pockets - Condesa, Roma', 'Easy to find your people'],
      outdoors:   ['Mountain hiking on weekends', 'Nearby pueblos mágicos', 'World-class parks'],
      safety:     ['Safer than most US cities in central zones', 'Walkable neighborhoods', 'Strong community vibe'],
    },
  },
  merida: {
    name: 'Mérida',
    img: CITY_THUMB('merida'),
    bands: { under_2k: 1300, '2_3_5': 1900, '3_5_5': 2800, '5_7_5': 4200, over_7_5: 6000 },
    facts: {
      healthcare: ['Star Medica and CMA hospitals', 'Specialists priced 70% below US', 'Quick appointments, no wait lists'],
      schools:    ['American School of Mérida', 'IB-accredited options', 'Spanish immersion top-rated'],
      cost:       ['Cheapest "safe-and-modern" city in country', 'Housing 60% below CDMX', 'Real estate market still rational'],
      safety:     ['Ranked safest city in Mexico for 10+ years', 'Walk anywhere day or night', 'Very low petty crime'],
      expats:     ['Largest US-family expat community', 'Newcomer Facebook groups very active', 'Weekly meetups in Centro'],
      lifestyle:  ['Colonial architecture everywhere', 'Cenotes 20 min from city', 'Slower, quieter rhythm'],
    },
  },
  oaxaca: {
    name: 'Oaxaca City',
    img: CITY_THUMB('oaxaca-city'),
    bands: { under_2k: 1100, '2_3_5': 1600, '3_5_5': 2400, '5_7_5': 3500, over_7_5: 5000 },
    facts: {
      cost:       ['Lowest cost-of-living of any mid-size city', '$1,200/mo covers a comfortable life', 'Real estate still affordable'],
      lifestyle:  ['UNESCO World Heritage city', "World's most acclaimed food scene", 'Mezcal, mole, monte albán'],
      outdoors:   ['Sierra Norte hiking just outside city', 'Pacific beaches 5 hr south', 'Year-round mild climate'],
      safety:     ['Calm city, low crime', 'Tight community feel', 'Easy to walk everywhere'],
    },
  },
  puerto_vallarta: {
    name: 'Puerto Vallarta',
    img: CITY_THUMB('puerto-vallarta'),
    bands: { under_2k: 1500, '2_3_5': 2200, '3_5_5': 3200, '5_7_5': 4800, over_7_5: 6800 },
    facts: {
      outdoors:   ['Bay surf, sailing, snorkeling', 'Sierra Madre hikes 30 min out', 'Beach-town childhood for kids'],
      expats:     ['LGBTQ+ family community is huge', '~30,000 US/Canadian residents', 'Marina hub for boaters'],
      lifestyle:  ['Cobblestone Zona Romántica', 'Beachfront cafes everywhere', 'Direct US flight access'],
    },
  },
  san_miguel: {
    name: 'San Miguel de Allende',
    img: CITY_THUMB('san-miguel-de-allende'),
    bands: { under_2k: 1500, '2_3_5': 2200, '3_5_5': 3400, '5_7_5': 5000, over_7_5: 7200 },
    facts: {
      expats:     ['Welcoming Americans for 70+ years', 'Mature bilingual school network', 'Easiest soft-landing in Mexico'],
      lifestyle:  ['UNESCO-listed colonial center', 'Arts and culinary scene', 'Climate near-perfect year-round'],
      schools:    ['Academia International', 'Smaller class sizes', 'Bilingual K-12'],
      safety:     ['Very low crime, quiet evenings', 'Tight expat-local mix', 'Walkable historic center'],
    },
  },
  guadalajara: {
    name: 'Guadalajara',
    img: CITY_THUMB('guadalajara'),
    bands: { under_2k: 1400, '2_3_5': 2000, '3_5_5': 3000, '5_7_5': 4500, over_7_5: 6500 },
    facts: {
      healthcare: ['Mexico\'s medical-tourism hub', 'Hospital Real San José', 'English-speaking specialists in every field'],
      remote:     ['Mexico\'s "Silicon Valley" - strong tech hubs', 'Reliable fiber everywhere', 'Most coworking spaces per capita'],
      cost:       ['Big-city options without CDMX prices', 'Excellent food at every tier', 'Universidad de Guadalajara hub'],
    },
  },
  playa_del_carmen: {
    name: 'Playa del Carmen',
    img: CITY_THUMB('playa-del-carmen'),
    bands: { under_2k: 1500, '2_3_5': 2200, '3_5_5': 3300, '5_7_5': 4900, over_7_5: 7000 },
    facts: {
      remote:     ['Nomad capital of Mexico', 'Reliable fiber + backup networks', 'Coworking on every block'],
      lifestyle:  ['Caribbean beach steps away', 'Diverse expat scene', 'Direct Cancún flights to most US cities'],
    },
  },
  tulum: {
    name: 'Tulum',
    img: CITY_THUMB('tulum'),
    bands: { under_2k: 1600, '2_3_5': 2300, '3_5_5': 3400, '5_7_5': 5000, over_7_5: 7100 },
    facts: {
      outdoors:   ['Cenotes everywhere', 'World-class diving + yoga', 'Mayan ruins on the beach'],
    },
  },
};

// iter 187.e - Household size is asked as Q3 (right after Age) so it
// can factor into the personalized budget math. Sits on Screen 13 in
// the codepath but logically slots between Age (Screen 4) and BP2
// (Screen 5) - see SCREEN_ORDER for the visible progression.
const HOUSEHOLD_OPTIONS = [
  { key: 'solo',   label: 'Just me',                        size: 1, dependents: 0 },
  { key: 'couple', label: 'Me + partner',                   size: 2, dependents: 1 },
  { key: 'small',  label: 'Small family (3-4 people)',      size: 3.5, dependents: 2.5 },
  { key: 'large',  label: 'Larger family (5+ people)',      size: 5, dependents: 4 },
];

// Budget category breakdown by tier. Numbers are USD/month, designed so
// the sum lands inside the budget band. Used for the Results page.
// iter 187.e - Household-size scaler applied to the variable-cost lines
// (food, transport, entertainment, utilities). Housing scales less
// aggressively because a 2-BR isn't twice a 1-BR. Healthcare scales
// per-person. Numbers stay inside the selected budget band on the
// solo path (size=1, scaler=1.0) and expand realistically as the
// household grows.
function buildBudgetBreakdown(budgetKey, householdKey = 'solo') {
  const tableByKey = {
    under_2k:  { housing:  700, food:  300, transport:  80, healthcare:  100, entertainment: 150, utilities: 100 },
    '2_3_5':   { housing: 1100, food:  450, transport: 120, healthcare:  150, entertainment: 250, utilities: 130 },
    '3_5_5':   { housing: 1500, food:  650, transport: 180, healthcare:  220, entertainment: 400, utilities: 180 },
    '5_7_5':   { housing: 2400, food:  900, transport: 250, healthcare:  300, entertainment: 600, utilities: 250 },
    over_7_5:  { housing: 3500, food: 1200, transport: 350, healthcare:  400, entertainment: 900, utilities: 350 },
  };
  const base = tableByKey[budgetKey] || tableByKey['2_3_5'];
  const hh = HOUSEHOLD_OPTIONS.find((h) => h.key === householdKey) || HOUSEHOLD_OPTIONS[0];
  // Scaler: 1.0 for solo, +0.30 per additional member on variable costs,
  // +0.12 per additional member on housing. Rounded to nearest $10.
  const varMult = 1 + 0.30 * (hh.size - 1);
  const housingMult = 1 + 0.12 * (hh.size - 1);
  const perPersonMult = hh.size;
  const round10 = (n) => Math.round(n / 10) * 10;
  return {
    housing:       round10(base.housing * housingMult),
    food:          round10(base.food * varMult),
    transport:     round10(base.transport * varMult),
    healthcare:    round10(base.healthcare * perPersonMult),
    entertainment: round10(base.entertainment * varMult),
    utilities:     round10(base.utilities * (1 + 0.15 * (hh.size - 1))),
  };
}

// Persona → results headline + Blueprint CTA line.
const PERSONA_RESULT = {
  retiree:    { head: "Your retirement doesn't start when you stop working. It starts when you choose where to live it. Here's your plan.",
                cta:  "Ready to get the details locked in? Your Blueprint covers your visa path, healthcare options, and cost certainty in full." },
  family:     { head: "Mexico is one of the most kid-friendly countries in the world. Here's what your family's life there actually looks like.",
                cta:  "Ready to make this real for your family? Your Blueprint has the school guides, neighborhood safety data, and visa path built specifically for you." },
  nomad:      { head: "Your next city is waiting. Here's where to start and why.",
                cta:  "Ready to test your first city? Your Blueprint maps the exact neighborhoods, visa steps, and budget for your situation." },
  empty:      { head: "You've earned this. Here's the life that's been waiting for you.",
                cta:  "Your Blueprint is ready. Everything you need to make this move - visa path, city match, budget, timeline." },
  divorced:   { head: "The hardest part was deciding to choose yourself. You did that. Here's what comes next.",
                cta:  "Your Blueprint is ready. Everything you need to make this move - visa path, city match, budget, timeline." },
  longterm:   { head: "You're more ready than you think. Here's your plan.",
                cta:  "Your Blueprint is ready. Everything you need to make this move - visa path, city match, budget, timeline." },
  sabbatical: { head: "You're more ready than you think. Here's your plan.",
                cta:  "Your Blueprint is ready. Everything you need to make this move - visa path, city match, budget, timeline." },
};

/* =========================================================================
 * Shared UI atoms.
 * ========================================================================= */

const Shell = ({ children, progress }) => (
  <div className="min-h-screen w-full bg-[#0D1F2D] text-[#F4ECD8] flex flex-col">
    <div className="fixed top-0 left-0 right-0 h-1 bg-white/10 z-50">
      <div className="h-full bg-[#E8A830] transition-[width] duration-500"
           style={{ width: `${progress}%` }} />
    </div>
    {children}
  </div>
);

const FadeIn = ({ children }) => (
  <div className="flex-1 flex flex-col w-full"
       style={{ animation: 'calcFadeIn 0.45s ease-out' }}>
    {children}
  </div>
);

// iter 144x - selected prop highlights the previously-chosen answer
// when the user navigates back to a question screen (either via the
// back arrow or the "Edit my answers" link on results).
const OptionCard = ({ onSelect, testid, selected = false, children }) => (
  <button
    onClick={onSelect}
    data-testid={testid}
    aria-pressed={selected}
    className={`w-full text-left ${
      selected
        ? 'bg-[#E8A830]/15 border-[#E8A830]'
        : 'bg-white/[0.04] hover:bg-white/[0.08] active:bg-[#E8A830]/15 border-white/15 hover:border-[#E8A830]'
    } border rounded-2xl px-5 py-4 md:py-5 transition-colors flex items-center gap-4`}
  >
    <span className="text-[#F4ECD8] text-base md:text-lg leading-snug flex-1">
      {children}
    </span>
    <span className="text-[#E8A830] text-lg flex-none">{selected ? '✓' : '→'}</span>
  </button>
);

// iter 144x - Top-left back chevron, rendered above-everything on
// question screens. Hidden on Screen 1, breakpoints, email gate, and
// results. Uses `fixed` so the on-screen position is identical across
// all parent layouts.
const BackArrow = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid="calc-back-arrow"
    aria-label="Go back one question"
    className="fixed top-3 left-3 z-40 w-10 h-10 flex items-center justify-center rounded-full bg-white/[0.06] hover:bg-white/[0.12] active:bg-[#E8A830]/20 border border-white/15 text-[#F4ECD8] text-xl transition-colors"
  >
    ←
  </button>
);

/* =========================================================================
 * Main component.
 * ========================================================================= */

export default function CostCalculator() {
  const navigate = useNavigate();
  const [screen, setScreen] = useState(1);

  // Answers persisted to localStorage on every advance so a refresh
  // doesn't dump the user back to Screen 1.
  const [answers, setAnswers] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(answers)); }
    catch { /* localStorage full / disabled */ }
  }, [answers]);

  const [leadId, setLeadId] = useState(null);
  const [emailForm, setEmailForm] = useState({ first_name: '', email: '' });
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // iter 165 - Per-visit random session id used by the funnel-event
  // ping below. Stored in a ref (not state) so it's stable across
  // re-renders and never causes a re-fire of the screen-view effect.
  const funnelSessionIdRef = useRef(null);
  if (funnelSessionIdRef.current === null) {
    funnelSessionIdRef.current = `calcv2_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  }

  // iter 187.c - Progressive-capture funnel identifier. Distinct from
  // the 64-char session_id above by design (per user decision #4) so
  // the four GA4 funnel events (v2_started, checkpoint_reached,
  // checkpoint_captured, v2_completed) can be joined in Explore without
  // coupling to whatever else the broader session_id is used for.
  // 16-char hex, sessionStorage-scoped so the id survives full page
  // reloads within the same tab but not new tabs.
  const funnelIdRef = useRef(null);
  if (funnelIdRef.current === null) {
    let fid = null;
    try { fid = window.sessionStorage?.getItem('calc_v2_funnel_id') || null; } catch { /* noop */ }
    if (!fid) {
      const bytes = new Uint8Array(8);
      (window.crypto || window.msCrypto).getRandomValues(bytes);
      fid = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
      try { window.sessionStorage?.setItem('calc_v2_funnel_id', fid); } catch { /* noop */ }
    }
    funnelIdRef.current = fid;
  }

  // iter 165 - Screen-view ping for the admin dropoff funnel. Fires
  // fire-and-forget on every setScreen() change. The backend upserts
  // on (session_id, screen) so a single visitor counts at most once
  // per screen regardless of how many times they navigate back to it.
  useEffect(() => {
    if (!screen) return;
    const sid = funnelSessionIdRef.current;
    const fid = funnelIdRef.current;
    fetch(`${API_URL}/api/tools/calculator/v2/funnel-event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sid, screen, funnel_session_id: fid }),
      keepalive: true,
    }).catch(() => { /* fire-and-forget; never block UI */ });
  }, [screen]);

  // iter 144x - Lock body scroll on every screen EXCEPT the results
  // screen, which is intentionally long (visa card + budget breakdown +
  // 3 city cards + CTAs) and must be scrollable on mobile.
  useEffect(() => {
    if (screen === 12) {
      document.body.classList.remove('overflow-hidden');
    } else {
      document.body.classList.add('overflow-hidden');
    }
    return () => document.body.classList.remove('overflow-hidden');
  }, [screen]);

  // iter 187.e - Logical screen ordering (Household added between Age
  // and BP2). Household is Screen 13 in code but slots visually at
  // position 5 in the funnel. `progress` and step counters read off
  // this array so the amber progress bar advances monotonically.
  const SCREEN_ORDER = [1, 2, 3, 4, 13, 5, 6, 7, 8, 9, 10, 11, 12];
  const orderIdx = Math.max(0, SCREEN_ORDER.indexOf(screen));
  const progress = Math.min(100, Math.round(((orderIdx + 1) / SCREEN_ORDER.length) * 100));

  const setAnswer = (k, v) => setAnswers((p) => ({ ...p, [k]: v }));

  /* ---------- Auto-advance helpers ---------- */
  // 300ms auto-advance after selection on choice screens.
  const advance = (next, delay = 300) => {
    setTimeout(() => setScreen(next), delay);
  };

  // iter 144x - Forward helper used by both the breakpoint timer and
  // the tap-anywhere handler. iter 187.c - Screen 5 (Visa BP2) now
  // routes to Screen 6 (progressive checkpoint reveal) instead of
  // skipping ahead. Screen 6 continues to Screen 12 (Results) via
  // `proceedToResults` on either the primary CTA or skip link.
  const advanceFromBreakpoint = () => {
    setScreen((s) => s + 1);
  };

  // Buffer / affirmation screens auto-advance after 5 seconds or on tap.
  const isBreakpoint = (s) => [3, 5, 8, 10].includes(s);
  useEffect(() => {
    if (!isBreakpoint(screen)) return undefined;
    const t = setTimeout(advanceFromBreakpoint, 5000);
    return () => clearTimeout(t);
  }, [screen, leadId]); // eslint-disable-line react-hooks/exhaustive-deps

  // iter 144x - Back navigation map. Full 6-question flow with the
  // Reveal (Screen 6) as a mid-flow checkpoint between Household and
  // BP2/reveal. Household (Screen 13) slots between Age (4) and BP2 (5).
  //   1 = opening hook
  //   2 = Persona (Q1)     → back to 1
  //   3 = BP1 persona affirmation (breakpoint)
  //   4 = Age (Q2)         → back to 2 (skip BP1)
  //  13 = Household (Q3)   → back to 4
  //   5 = BP2 visa (breakpoint)
  //   6 = Reveal (mid-flow checkpoint, not counted as a question)
  //   7 = Income (Q4)      → back to 6
  //   8 = BP3 visa proof (breakpoint)
  //   9 = Budget (Q5)      → back to 7
  //  10 = BP4 budget (breakpoint)
  //  11 = Priority (Q6)    → back to 9
  //  12 = Results          → back to 11
  const BACK_MAP = { 2: 1, 4: 2, 13: 4, 6: 13, 7: 6, 9: 7, 11: 9, 12: 11 };
  const goBack = () => {
    const target = BACK_MAP[screen];
    if (target) setScreen(target);
  };

  /* ---------- Computed (memoized) ---------- */
  const visaEarly = useMemo(() => buildVisaEligibilityCopy(answers.persona, answers.age),
                            [answers.persona, answers.age]);
  const incomeBp = useMemo(() => INCOME_BREAKPOINT[answers.income] || INCOME_BREAKPOINT.figuring,
                           [answers.income]);
  const budgetVerdict = useMemo(() => answers.budget
        ? buildBudgetVerdict(answers.budget, incomeBp.proof)
        : null,
      [answers.budget, incomeBp.proof]);
  const breakdown = useMemo(() => buildBudgetBreakdown(answers.budget || '2_3_5', answers.household || 'solo'),
                            [answers.budget, answers.household]);
  const cities = useMemo(() => {
    const ids = PRIORITY_TO_CITIES[answers.priority] || ['merida', 'mexico_city', 'oaxaca'];
    return ids.map((id) => CITY_INFO[id]).filter(Boolean);
  }, [answers.priority]);

  /* ---------- Progressive checkpoint (iter 187.g) ---------- */
  // iter 187.g - Mandatory email + first_name gate. Non-blocking design
  // (iter 187.c/d) is reversed here in favor of an approved required
  // capture so downstream email + Blueprint personalization can rely on
  // both fields being present. The `partial_lead` lifecycle stage is
  // KEPT (per user pick C-b) for visitors who reach the reveal card but
  // bounce before submitting - those still write a `partial_lead` row
  // via /v2/funnel-event so GA4 can see the drop-off.
  //
  // Back navigation (BACK_MAP[6] = 13) is preserved (per user pick B-a)
  // so users can revise Household without being trapped.
  const proceedFromCheckpoint = () => {
    setEmailError('');
    setNameError('');
    setScreen(7);
  };

  const submitEmail = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (submitting) return;
    const fn = (emailForm.first_name || '').trim();
    const em = (emailForm.email || '').trim().toLowerCase();

    // iter 187.g - Inline validation. Both fields required. Errors
    // render below their respective input; primary CTA is disabled
    // until both pass so the flow physically cannot advance without
    // valid capture.
    let hasError = false;
    if (!fn) {
      setNameError('Please enter your first name.');
      hasError = true;
    } else {
      setNameError('');
    }
    if (!em) {
      setEmailError('Please enter your email address.');
      hasError = true;
    } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) {
      setEmailError("That email doesn't look right - please check and try again.");
      hasError = true;
    } else {
      setEmailError('');
    }
    if (hasError) return;

    setSubmitting(true);
    try {
      const utm = getUtm();
      const promo = getStoredPromo();
      const resp = await fetch(`${API_URL}/api/tools/calculator/v2/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: em,
          first_name: fn,
          persona: answers.persona,
          age_range: answers.age,
          answers,
          utm_source: utm?.utm_source || null,
          utm_medium: utm?.utm_medium || null,
          utm_campaign: utm?.utm_campaign || null,
          promo_code: promo || null,
          funnel_session_id: funnelIdRef.current,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.lead_id) {
        // Server-side rejection (e.g. malformed email that slipped past
        // the client regex). Surface inline and hold at the checkpoint.
        setEmailError("We couldn't save that - please double-check your email and try again.");
        setSubmitting(false);
        return;
      }
      setLeadId(data.lead_id);
      setAnswer('email', em);
      setAnswer('first_name', fn);
      // Legacy event kept for now (some dashboards still filter on it);
      // the new checkpoint_captured is the canonical funnel event.
      trackCalculatorV2EmailCaptured({ persona: answers.persona, age: answers.age });
      trackCalculatorCheckpointCaptured({
        funnel_session_id: funnelIdRef.current,
        persona: answers.persona,
        age: answers.age,
      });
      setSubmitting(false);
      proceedFromCheckpoint();
    } catch {
      // Network failure at a mandatory gate - do NOT advance silently.
      // Show the inline error and let the user retry.
      setEmailError("Network hiccup - please try again in a moment.");
      setSubmitting(false);
    }
  };

  // Fire checkpoint_reached anonymously the first time we land on the
  // reveal-card screen. Everyone who reaches the checkpoint counts
  // here, regardless of whether they later submit an email.
  const checkpointReachedRef = useRef(false);
  useEffect(() => {
    if (screen !== 6 || checkpointReachedRef.current) return;
    checkpointReachedRef.current = true;
    const reveal = PERSONA_REVEAL[answers.persona] || PERSONA_REVEAL.longterm;
    const cityName = CITY_INFO[reveal.city]?.name;
    trackCalculatorCheckpointReached({
      funnel_session_id: funnelIdRef.current,
      persona: answers.persona,
      household: answers.age,  // household proxy - age range on this flow
      city_1: cityName,
    });
  }, [screen, answers.persona, answers.age]);

  /* ---------- Completion call (Screen 6 → results) ---------- */
  // iter 187.c - No longer requires a leadId. Fires whether or not
  // the visitor submitted an email at the checkpoint, so the anonymous
  // completion path is countable in `tool_calculator_leads_v2`.
  const completionSentRef = useRef(false);
  useEffect(() => {
    if (screen !== 12 || completionSentRef.current) return;
    completionSentRef.current = true;
    const capturedAtCheckpoint = !!leadId;
    trackCalculatorV2Completed({
      funnel_session_id: funnelIdRef.current,
      persona: answers.persona,
      budget: answers.budget,
      city_1: cities[0]?.name,
      priority: answers.priority,
      captured_at_checkpoint: capturedAtCheckpoint,
    });
    (async () => {
      try {
        await fetch(`${API_URL}/api/tools/calculator/v2/complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lead_id: leadId || null,
            email: answers.email || null,
            persona: answers.persona,
            age_range: answers.age,
            income_type: answers.income,
            budget_range: answers.budget,
            household_size: answers.household || null,
            priority: answers.priority,
            primary_visa: incomeBp.visa,
            city_1: cities[0]?.name || '',
            city_2: cities[1]?.name || null,
            city_3: cities[2]?.name || null,
            monthly_breakdown: breakdown,
            answers,
            funnel_session_id: funnelIdRef.current,
            captured_at_checkpoint: capturedAtCheckpoint,
          }),
        });
      } catch { /* non-blocking */ }
    })();
  }, [screen, leadId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------- Checkout routing ---------- */
  // Iter 151 - One-click Stripe checkout for quiz leads. We already
  // captured the user's email on screen 6, so the calculator CTA skips
  // the /checkout/blueprint_only signup form entirely and POSTs the
  // saved lead_id to /api/tools/checkout/from-lead, which returns a
  // Stripe Checkout Session URL. window.location.href hands the user
  // straight to checkout.stripe.com. After payment, the success page
  // (/relocation/blueprint/success) auto-redirects to the Blueprint
  // intake form where we collect the remaining info.
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const goCheckout = async (plan, promo = null) => {
    trackCalculatorV2CtaClicked({ plan });
    if (plan === 'community') {
      navigate(`/relocation/community${promo ? `?promo=${promo}` : ''}`);
      return;
    }
    if (checkoutLoading) return;
    // Without a lead_id we can't look up the email server-side. This
    // path should never trigger from the results screen (lead_id is
    // captured on screen 6 before results render), but if it ever does,
    // fall back to the signup form so the user isn't stranded.
    if (!leadId) {
      const qs = promo ? `?period=onetime&promo=${promo}` : '?period=onetime';
      navigate(`/checkout/blueprint_only${qs}`);
      return;
    }
    setCheckoutLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/tools/checkout/from-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lead_id: leadId,
          tier: plan,
          promo_code: promo || undefined,
        }),
      });
      const data = await r.json();
      if (r.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      // Backend rejected (lead expired, etc.) - fall back to signup form.
      const qs = promo ? `?period=onetime&promo=${promo}` : '?period=onetime';
      navigate(`/checkout/blueprint_only${qs}`);
    } catch {
      const qs = promo ? `?period=onetime&promo=${promo}` : '?period=onetime';
      navigate(`/checkout/blueprint_only${qs}`);
    } finally {
      setCheckoutLoading(false);
    }
  };

  /* ---------- Exit-intent on the results screen ---------- */
  const [exitOpen, setExitOpen] = useState(false);
  const [exitFired, setExitFired] = useState(false);
  const exitTriggerRef = useRef(null);
  useEffect(() => {
    if (screen !== 12 || exitFired) return undefined;
    const open = (trigger) => {
      if (exitFired) return;
      setExitFired(true);
      exitTriggerRef.current = trigger;
      setExitOpen(true);
      trackQuizExitIntentShown({ trigger });
    };
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget) open('mouseleave');
    };
    document.addEventListener('mouseout', onMouseOut);
    let idleTimer = setTimeout(() => open('idle_90s'), 90_000);
    const reset = () => {
      clearTimeout(idleTimer);
      if (!exitFired) idleTimer = setTimeout(() => open('idle_90s'), 90_000);
    };
    window.addEventListener('scroll', reset, { passive: true });
    window.addEventListener('touchstart', reset, { passive: true });
    return () => {
      document.removeEventListener('mouseout', onMouseOut);
      window.removeEventListener('scroll', reset);
      window.removeEventListener('touchstart', reset);
      clearTimeout(idleTimer);
    };
  }, [screen, exitFired]);

  const onExitConvert = () => {
    trackQuizExitIntentConverted({ trigger: exitTriggerRef.current });
    setExitOpen(false);
    goCheckout('blueprint_only', 'QUIZ50');
  };
  const onExitDismiss = () => {
    trackQuizExitIntentDismissed({ trigger: exitTriggerRef.current });
    setExitOpen(false);
  };

  /* ---------- Renderers ---------- */
  const result = PERSONA_RESULT[answers.persona] || PERSONA_RESULT.longterm;

  return (
    <Shell progress={progress}>
      <Helmet>
        <title>Mexico Cost of Living Calculator - Mundalo</title>
        <meta name="description" content="In 3 minutes, see exactly what your life in Mexico would cost. Visa path, monthly budget, ideal city - built for Americans planning the move." />
        <link rel="canonical" href="https://mundalo.com/mexico-cost-of-living-calculator" />
      </Helmet>
      <style>{`
        @keyframes calcFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Tap-anywhere for breakpoint screens */}
      <div
        className="flex-1 flex flex-col"
        onClick={isBreakpoint(screen) ? advanceFromBreakpoint : undefined}
      >

        {/* iter 144x - Back arrow shown only on question screens. */}
        {BACK_MAP[screen] && <BackArrow onClick={goBack} />}

        {/* ===== Screen 1 - Opening hook ===== */}
        {screen === 1 && (
          <FadeIn>
            <div className="flex-1 flex flex-col items-center justify-center px-5 py-8 max-w-2xl mx-auto w-full">
              <img src={BRAND_LOGO.white} alt="Mundalo" className="h-7 w-[140px] object-contain mb-10"
                   style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.45))' }} />
              <p data-testid="calc-context-line"
                 className="uppercase tracking-[0.22em] text-[11px] md:text-xs text-[#E8A830] mb-6 text-center"
                 style={{ fontFamily: "'Space Mono', 'JetBrains Mono', monospace" }}>
                For Americans Moving to Mexico
              </p>
              <h1 className="font-display italic text-3xl md:text-5xl leading-tight text-center text-[#F4ECD8] mb-6">
                Let's find out what your life in Mexico actually costs - and whether you're more ready than you think.
              </h1>
              <p className="font-mono uppercase tracking-[0.22em] text-xs md:text-sm text-[#E8A830] mb-10 text-center">
                3 minutes · visa path, budget, ideal city
              </p>
              <button
                data-testid="calc-cta-start"
                onClick={() => { trackCalculatorV2Started({ funnel_session_id: funnelIdRef.current }); setScreen(2); }}
                className="w-full bg-[#E8A830] hover:bg-[#f0b748] text-[#1a1a1a] font-semibold py-4 rounded-full text-base md:text-lg transition-colors"
              >
                Let&rsquo;s go
              </button>
            </div>
          </FadeIn>
        )}

        {/* ===== Screen 2 - Persona ===== */}
        {screen === 2 && (
          <FadeIn>
            <div className="flex-1 flex flex-col justify-center px-5 py-12 max-w-2xl mx-auto w-full">
              <p
                data-testid="calc-persona-progress"
                className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#F4ECD8]/60 text-center mb-4"
              >
                Step 1 of 6
              </p>
              <h2 className="font-display text-2xl md:text-4xl leading-snug text-center text-[#F4ECD8] mb-10">
                Which of these best describes your situation?
              </h2>
              <div className="space-y-3">
                {PERSONAS.map((p, i) => (
                  <OptionCard
                    key={p.key}
                    testid={`calc-persona-${i}`}
                    selected={answers.persona === p.key}
                    onSelect={() => { setAnswer('persona', p.key); advance(3); }}
                  >
                    <span className="block font-semibold">{p.label}</span>
                    <span className="block text-sm text-[#F4ECD8]/65">{p.sub}</span>
                  </OptionCard>
                ))}
              </div>
            </div>
          </FadeIn>
        )}

        {/* ===== Breakpoint 1 - Persona affirmation ===== */}
        {screen === 3 && (
          <FadeIn>
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 max-w-2xl mx-auto w-full text-center">
              <p data-testid="calc-bp-persona"
                 className="font-display italic text-xl md:text-3xl leading-snug text-[#F4ECD8] mb-10">
                {PERSONA_AFFIRMATION[answers.persona] || PERSONA_AFFIRMATION.longterm}
              </p>
              <p className="font-mono text-xs text-[#F4ECD8]/55 uppercase tracking-[0.16em]">
                Tap anywhere to continue
              </p>
            </div>
          </FadeIn>
        )}

        {/* ===== Screen 3 - Age ===== */}
        {screen === 4 && (
          <FadeIn>
            <div className="flex-1 flex flex-col justify-center px-5 py-12 max-w-2xl mx-auto w-full">
              <p
                data-testid="calc-age-progress"
                className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#F4ECD8]/60 text-center mb-4"
              >
                Step 2 of 6
              </p>
              <h2 className="font-display text-2xl md:text-4xl leading-snug text-center text-[#F4ECD8] mb-10">
                What age range are you?
              </h2>
              <div className="space-y-3">
                {AGE_OPTIONS.map((a, i) => (
                  <OptionCard
                    key={a}
                    testid={`calc-age-${i}`}
                    selected={answers.age === a}
                    onSelect={() => { setAnswer('age', a); advance(13); }}
                  >
                    {a}
                  </OptionCard>
                ))}
              </div>
            </div>
          </FadeIn>
        )}

        {/* ===== Screen 13 (iter 187.e) - Household size (Q3) ===== */}
        {screen === 13 && (
          <FadeIn>
            <div className="flex-1 flex flex-col justify-center px-5 py-12 max-w-2xl mx-auto w-full">
              <p
                data-testid="calc-household-progress"
                className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#F4ECD8]/60 text-center mb-4"
              >
                Step 3 of 6
              </p>
              <h2 className="font-display text-2xl md:text-4xl leading-snug text-center text-[#F4ECD8] mb-10">
                Who&rsquo;s making this move with you?
              </h2>
              <div className="space-y-3">
                {HOUSEHOLD_OPTIONS.map((h, i) => (
                  <OptionCard
                    key={h.key}
                    testid={`calc-household-${i}`}
                    selected={answers.household === h.key}
                    onSelect={() => { setAnswer('household', h.key); advance(5); }}
                  >
                    {h.label}
                  </OptionCard>
                ))}
              </div>
            </div>
          </FadeIn>
        )}

        {/* ===== Breakpoint 2 - Visa eligibility ===== */}
        {screen === 5 && (
          <FadeIn>
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 max-w-2xl mx-auto w-full text-center">
              <p className="font-mono uppercase tracking-[0.22em] text-[11px] md:text-xs text-[#E8A830] mb-4">
                Your visa path
              </p>
              <h2 className="font-display italic text-3xl md:text-5xl text-[#E8A830] mb-8 leading-tight">
                {visaEarly.visa}
              </h2>
              <p data-testid="calc-bp-visa"
                 className="font-display italic text-lg md:text-2xl leading-snug text-[#F4ECD8] mb-10 max-w-xl">
                {visaEarly.copy}
              </p>
              <p className="font-mono text-xs text-[#F4ECD8]/55 uppercase tracking-[0.16em]">
                Tap anywhere to continue
              </p>
            </div>
          </FadeIn>
        )}

        {/* ===== Screen 6 - iter 187.c Progressive checkpoint (reveal + optional email) ===== */}
        {screen === 6 && (() => {
          const reveal = PERSONA_REVEAL[answers.persona] || PERSONA_REVEAL.longterm;
          const revealCity = CITY_INFO[reveal.city];
          return (
            <FadeIn>
              <div
                data-testid="calc-checkpoint-reveal"
                className="flex-1 flex flex-col justify-center px-5 py-10 max-w-md mx-auto w-full"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Mid-flow checkpoint - not counted as a numbered step. */}
                <p
                  data-testid="calc-checkpoint-progress"
                  className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#F4ECD8]/60 text-center mb-6"
                >
                  Checkpoint · 3 questions to go
                </p>

                <p className="font-mono uppercase tracking-[0.22em] text-[11px] text-[#E8A830] text-center mb-3">
                  Your match
                </p>
                <h2
                  data-testid="calc-checkpoint-city"
                  className="font-display italic text-4xl md:text-5xl text-[#F4ECD8] text-center mb-6 leading-tight"
                >
                  {revealCity?.name || 'Mérida'}
                </h2>
                {revealCity?.img && (
                  <div className="rounded-xl overflow-hidden mb-6 aspect-[4/3] bg-black/20">
                    <img
                      src={revealCity.img}
                      alt={revealCity.name}
                      className="w-full h-full object-cover opacity-90"
                    />
                  </div>
                )}
                <p
                  data-testid="calc-checkpoint-descriptor"
                  className="text-[#F4ECD8]/85 text-base leading-relaxed text-center mb-8"
                >
                  Based on what you&rsquo;ve told me so far - {reveal.descriptor} - {revealCity?.name} is a strong match for your situation.
                </p>

                <div className="border-t border-[#F4ECD8]/15 pt-6 mb-6">
                  <p className="font-mono uppercase tracking-[0.2em] text-[10px] text-[#F4ECD8]/60 text-center mb-2">
                    Get your full breakdown
                  </p>
                  {/* iter 187.g - Mandatory email + first_name gate. One-line
                       explanation, no urgency or scarcity language. */}
                  <p
                    data-testid="calc-checkpoint-explainer"
                    className="text-[#F4ECD8]/75 text-sm leading-relaxed text-center mb-4"
                  >
                    Enter your email to see your full personalized breakdown.
                  </p>
                  <div className="space-y-3">
                    <div>
                      <input
                        type="text"
                        placeholder="First name"
                        aria-required="true"
                        required
                        data-testid="calc-checkpoint-first-name"
                        value={emailForm.first_name}
                        onChange={(e) => {
                          setEmailForm((f) => ({ ...f, first_name: e.target.value }));
                          if (nameError) setNameError('');
                        }}
                        className="w-full bg-transparent border border-[#F4ECD8]/30 focus:border-[#E8A830] text-[#F4ECD8] placeholder:text-[#F4ECD8]/40 rounded-lg px-4 py-3 outline-none transition-colors"
                      />
                      {nameError && (
                        <p
                          data-testid="calc-checkpoint-name-error"
                          className="mt-1.5 text-xs text-[#E8A830]"
                        >
                          {nameError}
                        </p>
                      )}
                    </div>
                    <div>
                      <input
                        type="email"
                        placeholder="your@email.com"
                        aria-required="true"
                        required
                        data-testid="calc-checkpoint-email"
                        value={emailForm.email}
                        onChange={(e) => {
                          setEmailForm((f) => ({ ...f, email: e.target.value }));
                          if (emailError) setEmailError('');
                        }}
                        className="w-full bg-transparent border border-[#F4ECD8]/30 focus:border-[#E8A830] text-[#F4ECD8] placeholder:text-[#F4ECD8]/40 rounded-lg px-4 py-3 outline-none transition-colors"
                      />
                      {emailError && (
                        <p
                          data-testid="calc-checkpoint-email-error"
                          className="mt-1.5 text-xs text-[#E8A830]"
                        >
                          {emailError}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={submitEmail}
                  disabled={submitting}
                  data-testid="calc-checkpoint-primary"
                  className="w-full bg-[#E8A830] hover:bg-[#f0b748] disabled:opacity-60 text-[#1a1a1a] font-semibold py-4 rounded-full transition-colors"
                >
                  {submitting ? 'One moment…' : 'Continue to next question →'}
                </button>
              </div>
            </FadeIn>
          );
        })()}

        {/* ===== Screen 7 - Income / work ===== */}
        {screen === 7 && (
          <FadeIn>
            <div className="flex-1 flex flex-col justify-center px-5 py-12 max-w-2xl mx-auto w-full">
              <p
                data-testid="calc-income-progress"
                className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#F4ECD8]/60 text-center mb-4"
              >
                Step 4 of 6
              </p>
              <h2 className="font-display text-2xl md:text-4xl leading-snug text-center text-[#F4ECD8] mb-10">
                What will your income look like in Mexico?
              </h2>
              <div className="space-y-3">
                {INCOME_OPTIONS.map((inc, i) => (
                  <OptionCard
                    key={inc.key}
                    testid={`calc-income-${i}`}
                    selected={answers.income === inc.key}
                    onSelect={() => { setAnswer('income', inc.key); advance(8); }}
                  >
                    {inc.label}
                  </OptionCard>
                ))}
              </div>
            </div>
          </FadeIn>
        )}

        {/* ===== Breakpoint 3 - Visa path + proof of funds ===== */}
        {screen === 8 && (
          <FadeIn>
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 max-w-2xl mx-auto w-full text-center">
              <p className="font-mono uppercase tracking-[0.22em] text-[11px] md:text-xs text-[#E8A830] mb-4">
                Your visa, confirmed
              </p>
              <h2 className="font-display italic text-3xl md:text-5xl text-[#E8A830] mb-8 leading-tight">
                {incomeBp.visa}
              </h2>
              <p data-testid="calc-bp-income"
                 className="font-display italic text-lg md:text-2xl leading-snug text-[#F4ECD8] mb-10 max-w-xl">
                {incomeBp.copy}
              </p>
              <p className="font-mono text-xs text-[#F4ECD8]/55 uppercase tracking-[0.16em]">
                Tap anywhere to continue
              </p>
            </div>
          </FadeIn>
        )}

        {/* ===== Screen 9 - Monthly budget ===== */}
        {screen === 9 && (
          <FadeIn>
            <div className="flex-1 flex flex-col justify-center px-5 py-12 max-w-2xl mx-auto w-full">
              <p
                data-testid="calc-budget-progress"
                className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#F4ECD8]/60 text-center mb-4"
              >
                Step 5 of 6
              </p>
              <h2 className="font-display text-2xl md:text-4xl leading-snug text-center text-[#F4ECD8] mb-10">
                What does your monthly budget look like in Mexico?
              </h2>
              <div className="space-y-3">
                {BUDGET_OPTIONS.map((b, i) => (
                  <OptionCard
                    key={b.key}
                    testid={`calc-budget-${i}`}
                    selected={answers.budget === b.key}
                    onSelect={() => { setAnswer('budget', b.key); advance(10); }}
                  >
                    {b.label}
                  </OptionCard>
                ))}
              </div>
            </div>
          </FadeIn>
        )}

        {/* ===== Breakpoint 4 - Budget validation + city preview ===== */}
        {screen === 10 && budgetVerdict && (
          <FadeIn>
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 max-w-2xl mx-auto w-full text-center">
              <p className="font-mono uppercase tracking-[0.22em] text-[11px] md:text-xs text-[#E8A830] mb-4">
                Budget verdict
              </p>
              <p data-testid="calc-bp-budget"
                 className="font-display italic text-lg md:text-2xl leading-snug text-[#F4ECD8] mb-10 max-w-xl">
                {budgetVerdict.copy}
              </p>
              <p className="font-mono text-xs text-[#F4ECD8]/55 uppercase tracking-[0.16em]">
                Tap anywhere to continue
              </p>
            </div>
          </FadeIn>
        )}

        {/* ===== Screen 11 - Priority ===== */}
        {screen === 11 && (
          <FadeIn>
            <div className="flex-1 flex flex-col justify-center px-5 py-12 max-w-2xl mx-auto w-full">
              <p
                data-testid="calc-priority-progress"
                className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#F4ECD8]/60 text-center mb-4"
              >
                Step 6 of 6
              </p>
              <h2 className="font-display text-2xl md:text-4xl leading-snug text-center text-[#F4ECD8] mb-10">
                What matters most in your city choice?
              </h2>
              <div className="space-y-3">
                {PRIORITY_OPTIONS.map((p, i) => (
                  <OptionCard
                    key={p.key}
                    testid={`calc-priority-${i}`}
                    selected={answers.priority === p.key}
                    onSelect={() => {
                      setAnswer('priority', p.key);
                      // iter 187.c (updated) - Reveal (Screen 6) now sits
                      // between Age and Income, so Priority always advances
                      // straight to Results.
                      advance(12);
                    }}
                  >
                    {p.label}
                  </OptionCard>
                ))}
              </div>
            </div>
          </FadeIn>
        )}

        {/* ===== Screen 12 - Results ===== */}
        {screen === 12 && (
          <FadeIn>
            <div className="flex-1 flex flex-col items-center px-5 py-12 max-w-3xl mx-auto w-full">
              <img src={BRAND_LOGO.white} alt="Mundalo" className="h-7 w-[140px] object-contain mb-8"
                   style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.45))' }} />
              <h1 data-testid="calc-result-headline"
                  className="font-display text-3xl md:text-5xl leading-tight text-[#F4ECD8] text-center mb-4">
                {result.head}
              </h1>
              {/* iter 144x - Edit-my-answers shortcut. Sends the user back to
                  Screen 2 (persona) without clearing localStorage, so every
                  previously-picked option is highlighted via OptionCard's
                  `selected` prop and they can change one answer and walk
                  forward through the breakpoints again. The advance helper
                  skips the email gate when leadId is set. */}
              <button
                type="button"
                data-testid="calc-edit-answers"
                onClick={() => { completionSentRef.current = false; setScreen(2); }}
                className="font-mono uppercase tracking-[0.22em] text-[11px] text-[#E8A830] hover:text-[#f0b748] mb-10 transition-colors"
              >
                ← Edit my answers
              </button>

              {/* Section 1 - Visa (iter 187.o: reduced free view).
                  The exact proof-of-funds dollar figure and approval-weeks
                  stat are now gated. Free viewers see the visa NAME +
                  the persona-driven prose. Full requirements list ships
                  in the paid Blueprint. */}
              <section data-testid="calc-result-visa" className="w-full mb-10">
                <p className="font-mono uppercase tracking-[0.22em] text-[11px] text-[#E8A830] mb-3">Your visa</p>
                <div className="bg-white/[0.04] border-2 border-[#E8A830] rounded-2xl p-6 md:p-7">
                  <h3 className="font-display text-2xl md:text-3xl text-[#F4ECD8] mb-3">{incomeBp.visa}</h3>
                  <p className="text-[#F4ECD8]/85 text-base leading-relaxed">
                    {FREE_VIEW_VISA_COPY[answers.income] || FREE_VIEW_VISA_COPY.figuring}
                  </p>
                </div>
              </section>

              {/* Section 2 - Budget total (iter 187.o: single rounded
                  monthly total instead of the 6-category breakdown.
                  Category-by-category breakdown ships in the paid
                  Blueprint - listed in the tease block below.) */}
              <section data-testid="calc-result-budget" className="w-full mb-10">
                <p className="font-mono uppercase tracking-[0.22em] text-[11px] text-[#E8A830] mb-3">Your rough monthly cost</p>
                <div className="bg-white/[0.04] border border-white/15 rounded-2xl p-6 md:p-7 flex items-baseline justify-between">
                  <span className="text-[#F4ECD8]/85 text-sm md:text-base">Estimated total</span>
                  <span
                    data-testid="calc-result-budget-total"
                    className="text-[#E8A830] font-semibold text-2xl md:text-3xl"
                  >
                    ~${Math.round(Object.values(breakdown).reduce((a, b) => a + b, 0) / 100) * 100}/mo
                  </span>
                </div>
              </section>

              {/* Section 3 - Single top city (iter 187.o: reduced from
                  3 ranked cities to 1. Multi-city shortlist ships in the
                  paid Blueprint - listed in the tease block below.) */}
              <section data-testid="calc-result-cities" className="w-full mb-10">
                <p className="font-mono uppercase tracking-[0.22em] text-[11px] text-[#E8A830] mb-3">Your recommended city</p>
                <div className="space-y-4">
                  {cities.slice(0, 1).map((c, i) => {
                    const monthlyBand = c.bands[answers.budget] || c.bands['2_3_5'];
                    const facts = (c.facts && c.facts[answers.priority]) || [];
                    return (
                      <div key={c.name + i} className="bg-white/[0.04] border border-white/15 rounded-2xl overflow-hidden">
                        <div className="flex items-center gap-4 p-4">
                          <img
                            src={c.img}
                            alt=""
                            loading="lazy"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            className="w-16 h-16 rounded-lg object-cover flex-none bg-white/10"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-display text-xl text-[#F4ECD8]">{c.name}</p>
                            <p className="text-sm text-[#E8A830]">~${monthlyBand.toLocaleString()}/mo</p>
                          </div>
                        </div>
                        {facts.length > 0 && (
                          <ul className="px-4 pb-4 space-y-1.5">
                            {facts.map((f, j) => (
                              <li key={j} className="text-sm text-[#F4ECD8]/80 flex gap-2">
                                <span className="text-[#E8A830] flex-none">•</span>
                                <span>{f}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Section 3b - Curiosity-gap tease (iter 187.o).
                  Locked copy: "That's the rough sketch. Your full
                  Blueprint takes it from sketch to plan." Followed by
                  an itemized list of what's gated behind the paid
                  Blueprint. Sits directly above the Blueprint CTA
                  block so the two flow as one conversion moment. */}
              <section data-testid="calc-result-tease" className="w-full mb-10">
                <div className="bg-[#E8A830]/[0.06] border border-[#E8A830]/25 rounded-2xl p-6 md:p-7">
                  <p className="font-display italic text-lg md:text-xl text-[#F4ECD8] mb-4 leading-snug">
                    That&apos;s the rough sketch. Your full Blueprint takes it from sketch to plan.
                  </p>
                  <ul className="space-y-2 text-sm md:text-base text-[#F4ECD8]/85">
                    <li className="flex gap-2"><span className="text-[#E8A830] flex-none">•</span><span>Exact visa path and proof-of-funds requirement</span></li>
                    <li className="flex gap-2"><span className="text-[#E8A830] flex-none">•</span><span>School options for your household</span></li>
                    <li className="flex gap-2"><span className="text-[#E8A830] flex-none">•</span><span>Neighborhood shortlist tailored to your priorities</span></li>
                    <li className="flex gap-2"><span className="text-[#E8A830] flex-none">•</span><span>Full month-by-month budget breakdown</span></li>
                    <li className="flex gap-2"><span className="text-[#E8A830] flex-none">•</span><span>Moving-cost estimate for your move</span></li>
                  </ul>
                </div>
              </section>

              {/* Section 4 - Next steps + Blueprint CTA (iter 145 Section 5)
                  Primary CTA goes straight to Stripe checkout for Blueprint
                  Only ($199). Secondary "I'll keep exploring" link reveals a
                  one-shot today-only 50% off (QUIZ50) offer with a midnight
                  countdown - appears once per session. */}
              <section data-testid="calc-result-cta" className="w-full mb-6">
                <p className="text-[#F4ECD8]/85 text-base leading-relaxed mb-5 text-center">
                  {result.cta}
                </p>
                <div className="bg-white/[0.04] border-2 border-[#E8A830] rounded-2xl p-5 md:p-6 mb-3">
                  <p className="font-mono uppercase tracking-[0.18em] text-xs text-[#E8A830] mb-2">Blueprint Only</p>
                  <p className="text-[#F4ECD8] mb-4">
                    <span className="text-3xl md:text-4xl font-semibold">$199</span>
                    <span className="text-sm ml-2 opacity-70">one-time</span>
                  </p>
                  <button
                    data-testid="calc-cta-blueprint"
                    onClick={() => goCheckout('blueprint_only')}
                    disabled={checkoutLoading}
                    className="w-full bg-[#E8A830] hover:bg-[#f0b748] disabled:opacity-70 disabled:cursor-wait text-[#1a1a1a] font-semibold py-3.5 rounded-full transition-colors"
                  >
                    {checkoutLoading ? 'Redirecting to checkout…' : 'Get Your Blueprint'}
                  </button>
                </div>

                <KeepExploringOffer onConvert={() => goCheckout('blueprint_only', 'QUIZ50')} />
              </section>
            </div>
          </FadeIn>
        )}

      </div>

      {/* ===== Exit-intent overlay (QUIZ50 → $99) ===== */}
      {exitOpen && (
        <div
          data-testid="calc-exit-intent-overlay"
          onClick={onExitDismiss}
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center px-5 py-8"
        >
          <div onClick={(e) => e.stopPropagation()}
               className="w-full max-w-md bg-[#0D1F2D] border-2 border-[#E8A830] rounded-2xl p-7 md:p-8 shadow-2xl">
            <p className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#E8A830] mb-3">One last thing</p>
            <h2 className="font-display text-3xl md:text-4xl leading-tight text-[#F4ECD8] mb-4">
              Wait - before you go.
            </h2>
            <p className="text-sm md:text-base text-[#F4ECD8]/85 leading-relaxed mb-6">
              We know this is a big decision. Take $100 off your Blueprint today - only for people who finished the calculator. One-time, expires when you close this tab.
            </p>
            <div className="bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 mb-6">
              <p className="text-[#F4ECD8]">
                <span className="line-through opacity-50 mr-2">$199</span>
                <span className="text-3xl md:text-4xl font-semibold">$99</span>
                <span className="text-xs ml-2 opacity-70">with QUIZ50</span>
              </p>
            </div>
            <button
              onClick={onExitConvert}
              data-testid="calc-exit-cta"
              className="w-full bg-[#E8A830] hover:bg-[#f0b748] text-[#1a1a1a] font-semibold py-3.5 rounded-full transition-colors mb-3"
            >
              Get my Blueprint for $99
            </button>
            <button
              onClick={onExitDismiss}
              data-testid="calc-exit-dismiss"
              className="w-full text-center text-sm text-[#F4ECD8]/55 hover:text-[#F4ECD8]/80 font-mono py-1 transition-colors"
            >
              No thanks, I&rsquo;ll figure it out myself.
            </button>
          </div>
        </div>
      )}
    </Shell>
  );
}
